import { Button, Input, Modal, Switch, Typography } from '@faceup/ui-base'
import { type AiPromptName, fillAiPrompt } from '@faceup/utils'
import { useState } from 'react'
import { getAiPromptConfig } from './aiPromptDefinitions'

const { Title, Text } = Typography

type AiPromptPreviewModalProps = {
  name: AiPromptName
  content: string | null
  title: string
  onClose: () => void
}

/** Shows the prompt with its variables and conditional blocks filled the way the backend does. */
export const AiPromptPreviewModal = ({
  name,
  content,
  title,
  onClose,
}: AiPromptPreviewModalProps) => {
  const config = getAiPromptConfig(name)
  const [variables, setVariables] = useState(() =>
    Object.fromEntries(Object.entries(config.variables).map(([key, { example }]) => [key, example]))
  )
  const [conditions, setConditions] = useState(() =>
    Object.fromEntries(Object.keys(config.conditions).map(key => [key, false]))
  )

  return (
    <Modal
      title={title}
      open={content !== null}
      onClose={onClose}
      width={1000}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <Title level={5} className='mb-3'>
        Test variables
      </Title>
      <Text type='secondary' className='mb-3 block text-xs'>
        Clear a value to see the prompt when the platform has nothing to fill in.
      </Text>
      <div className='flex flex-col gap-3'>
        {Object.entries(config.conditions).map(([key, { description }]) => (
          <div key={key} className='flex items-center gap-2'>
            <label htmlFor={`prompt-preview-${key}`}>
              <Text strong>{description}</Text>
            </label>
            <Switch
              id={`prompt-preview-${key}`}
              checked={conditions[key]}
              onChange={checked => setConditions({ ...conditions, [key]: checked })}
            />
          </div>
        ))}
        {Object.keys(config.variables).map(key => (
          <div key={key}>
            <label htmlFor={`prompt-preview-${key}`}>
              <Text code>{`{${key}}`}</Text>
            </label>
            <Input.TextArea
              id={`prompt-preview-${key}`}
              value={variables[key]}
              onChange={event => setVariables({ ...variables, [key]: event.target.value })}
              autoSize={{ minRows: 1, maxRows: 6 }}
              className='mt-1'
            />
          </div>
        ))}
      </div>

      <Title level={5} className='mt-4 mb-3'>
        Preview output
      </Title>
      <pre className='max-h-[40vh] overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-colorBorder p-4 font-mono'>
        {content && fillAiPrompt(name, content, { variables, conditions })}
      </pre>
    </Modal>
  )
}
